import { APP_INITIALIZER, ApplicationConfig, provideZoneChangeDetection } from '@angular/core';
import {provideRouter, withComponentInputBinding, withRouterConfig} from '@angular/router';
import { RouteReuseStrategy } from '@angular/router';
import { APP_BASE_HREF } from '@angular/common';
import { CustomReuseStrategy } from './custom-reuse-strategy'; 
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { provideOAuthClient } from 'angular-oauth2-oidc';
import { OAuthService } from 'angular-oauth2-oidc';
import { AuthRefreshInterceptor } from './service/auth/auth-refresh.interceptor';
import { DeviceIdInterceptor } from './service/device/device-id.interceptor';
import { appBaseHrefFactory } from './app-base-href';

import { routes } from './app.routes';
import { RuntimeConfigService } from './service/config/runtime-config.service';
import { ConnectivityService } from './service/connectivity/connectivity.service';
import { LocalDatabaseService } from './service/storage/local-database.service';
import { ProgressSyncService } from './service/sync/progress-sync.service';
import { setupOidcDiscovery } from './oidc-app.initializer';
import { oidcDiscoveryState } from './oidc-discovery.state';
import { setupE2eDeepLink } from './e2e-deep-link.initializer';
import { AuthService } from './service/auth/auth.service';
import { RestService } from './service/rest/rest.service';
import { ContentPackService } from './service/content-pack/content-pack.service';
import { Router } from '@angular/router';
import { OfflineApiInterceptor } from './service/connectivity/offline-api.interceptor';

function initApp(
  runtimeConfig: RuntimeConfigService,
  oauth: OAuthService,
  connectivity: ConnectivityService,
  localDatabase: LocalDatabaseService,
  progressSync: ProgressSyncService,
  auth: AuthService,
  rest: RestService,
  contentPacks: ContentPackService,
  router: Router
) {
  return async () => {
    await runtimeConfig.load();
    await connectivity.start();
    if (connectivity.isOnline) {
      await setupOidcDiscovery(runtimeConfig, oauth);
    }
    connectivity.connected$.subscribe((online) => {
      if (online && !oidcDiscoveryState.loaded && runtimeConfig.oidcConfigured) {
        void setupOidcDiscovery(runtimeConfig, oauth);
      }
    });
    void localDatabase.init();
    await progressSync.start();
    // Restore "stay signed in" before AuthGuard runs so a fresh app launch goes
    // straight to the dashboard instead of bouncing through /login.
    try {
      await auth.restoreSessionFromStorage();
    } catch {
      /* never block startup on auth restore */
    }
    await setupE2eDeepLink(auth, router, rest, localDatabase, progressSync, contentPacks)();
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideOAuthClient(),
    {
      provide: APP_INITIALIZER,
      useFactory: initApp,
      deps: [
        RuntimeConfigService,
        OAuthService,
        ConnectivityService,
        LocalDatabaseService,
        ProgressSyncService,
        AuthService,
        RestService,
        ContentPackService,
        Router,
      ],
      multi: true,
    },
    provideRouter(routes, withComponentInputBinding(),withRouterConfig({paramsInheritanceStrategy: 'always'})),
    { provide: APP_BASE_HREF, useFactory: appBaseHrefFactory },
    { provide: RouteReuseStrategy, useClass: CustomReuseStrategy },
    provideHttpClient(withInterceptorsFromDi()),
    { provide: HTTP_INTERCEPTORS, useClass: OfflineApiInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: DeviceIdInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AuthRefreshInterceptor, multi: true },
  ]
};
